import { makeStyles } from '@material-ui/styles'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import {
  maxValueValidator,
  minValueValidator,
  rangeValueValidator,
  vnidRangeValidator,
} from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import { createNetwork, createSubnet, listNetworks } from './actions'
import AdminStatePicklist from './AdminStatePicklist'
import { getPhysicalNetworkRequestBody } from './helpers'
import { getIPv6AdditionalFields } from './IPv6AddressConfigurationModePicklist'
import PhysicalNetworkLabelPicklist from './PhysicalNetworkLabelPicklist'
import PhysicalNetworkTypePicklist from './PhysicalNetworkTypePicklist'
import { AdvancedSubnetFields, BasicSubnetFields } from './SubnetFormFields'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { trackEvent } from 'utils/tracking'
import ZonePicklist from './dns/records/ZonePicklist'
import { DNS_ZONE_INFO, MAKE_SHARED_INFO } from './constants'
import { getBlueprints } from '../infrastructure/actions'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  addRoute: Route
}

export default function CreatePhysicalNetworkModal({ addRoute }: Props) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const [createdNetworkId, setCreatedNetworkId] = useState(null)
  const [virtualNetworkSetting, setVirtualNetworkSetting] = useState(null)
  const defaultParams = {
    networkType: '',
    externalNetworkType: 'flat',
    segmentationId: '',
    name: '',
    description: '',
    shared: false,
    adminState: 'Up',
    createSubnet: true,
    ipVersion: 4,
    cidr: '',
    gatewayIp: '',
    disableGateway: false,
    allocationPools: [],
    dnsNameServers: [],
    hostRoutes: [],
    enableDhcp: true,
    ipv6AddressConfigurationMode: '',
    external: false,
    networkLabel: '',
    enablePortSecurity: true,
    mtu: 1500,
    dns_domain: '',
    dns_publish_fixed_ip: false,
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { reload } = useListAction(listNetworks, {})
  const { update, updating, error, reset } = useUpdateAction(createNetwork)
  const {
    update: updateSubnet,
    updating: updatingSubnet,
    error: subnetError,
    reset: resetSubnet,
  } = useUpdateAction(createSubnet)

  const createNetworkFn = async (params) => {
    const body = getPhysicalNetworkRequestBody(params)
    const { success, response } = await update({ body })
    return { success, response }
  }

  const createSubnetFn = async (params, networkId) => {
    const allocationPools = params.allocationPools.map((entry) => {
      const [start, end] = entry.replace(/\s/g, '').split('-')
      return {
        start,
        end,
      }
    })
    const hostRoutes = params.hostRoutes.map((entry) => {
      const [cidr, nexthop] = entry.replace(/\s/g, '').split(',')
      return {
        destination: cidr,
        nexthop,
      }
    })
    const body = {
      subnet: {
        name: params.subnetName,
        network_id: networkId,
        ip_version: params.ipVersion,
        cidr: params.cidr,
        gateway_ip: params.disableGateway ? null : params.gatewayIp ? params.gatewayIp : undefined,
        allocation_pools: allocationPools.length ? allocationPools : undefined,
        dns_nameservers: params.dnsNameServers.length ? params.dnsNameServers : undefined,
        host_routes: hostRoutes.length ? hostRoutes : undefined,
        enable_dhcp: params.enableDhcp,
        dns_publish_fixed_ip: params.dns_publish_fixed_ip,
        ...getIPv6AdditionalFields(params),
      },
    }
    const { success } = await updateSubnet({ body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    if (createdNetworkId && params.createSubnet) {
      const { success } = await createSubnetFn(params, createdNetworkId)
      if (success) {
        reload(true, true)
        handleClose()
      }
    } else if (createdNetworkId && !params.createSubnet) {
      handleClose()
    } else {
      const { success, response } = await createNetworkFn(params)
      if (success) {
        trackEvent(`Create Network - Finished`)
      }
      if (params.createSubnet) {
        const networkId = response?.id
        setCreatedNetworkId(networkId)
        const { success: subnetSuccess } = await createSubnetFn(params, networkId)
        if (subnetSuccess) {
          trackEvent(`Create Subnet - Finished`)
          reload(true, true)
          handleClose()
        } else {
          return
        }
      }
      if (success) {
        handleClose()
      }
    }
  }, [params])

  const fetchBlueprints = async () => {
    try {
      const blueprints = (await getBlueprints()) as any[]
      const blueprintSettings = blueprints?.[0]
      setVirtualNetworkSetting(blueprintSettings?.virtualNetworking)
    } catch (error) {
      console.error('Failed to fetch blueprints:', error)
    }
  }

  useEffect(() => {
    fetchBlueprints()
  }, [])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetSubnet()
    setCreatedNetworkId(null)
    history.push(routeManager.routes.openstack.networks.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Network`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingSubnet}
      error={error || subnetError}
      submitTitle={`Create Network`}
    >
      <>
        <ServiceUnhealthyInfo action="Network Creation" network />
        <FormFieldSection title="Network Configuration">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          <PicklistField
            DropdownComponent={PhysicalNetworkLabelPicklist}
            id="networkLabel"
            label="Network Label"
            value={params.networkLabel}
            onChange={getParamsUpdater('networkLabel')}
            info="Select the network label that maps this network to the corresponding physical network on your hypervisors"
            required
          />
          <CheckboxField
            id="external"
            label="Allow use of Public IPs (External Network)"
            value={params.external}
            onChange={getParamsUpdater('external')}
            info="Set checked if the network should be able to assign public IPs (external network)"
          />
          <PicklistField
            DropdownComponent={PhysicalNetworkTypePicklist}
            id="networkType"
            label="Network Type"
            value={params.networkType}
            onChange={getParamsUpdater('networkType')}
            required
          />
          {['vlan'].includes(params.networkType) && (
            <TextField
              id="segmentationId"
              label={'VLAN ID'}
              onChange={getParamsUpdater('segmentationId')}
              value={params.segmentationId}
              validations={[
                rangeValueValidator(1, 4094),
                vnidRangeValidator(virtualNetworkSetting),
              ]}
              required
              type="number"
              min="0"
            />
          )}
          <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
          <PicklistField
            DropdownComponent={ZonePicklist}
            id="zoneId"
            label="DNS Zone"
            onChange={getParamsUpdater('dns_domain')}
            value={params.dns_domain}
            info={DNS_ZONE_INFO}
            setLabelAsValue={true}
          />
          <TextField
            id="mtu"
            label="MTU"
            onChange={(value) => getParamsUpdater('mtu')(value as number)}
            validations={[minValueValidator(1280), maxValueValidator(9000)]}
            value={params.mtu}
            type="number"
            info="Maximum Transmission Unit (MTU) for the network. MTU must be between 1280 and 9000"
          />
          <CheckboxField
            id="enablePortSecurity"
            label="Enable Port Security"
            value={params.enablePortSecurity}
            onChange={getParamsUpdater('enablePortSecurity')}
            info="If checked, newly created ports on this network will have port security enabled by default."
          />
          <CheckboxField
            id="shared"
            label="Make Shared (Across All Tenants)"
            value={params.shared}
            onChange={getParamsUpdater('shared')}
            info={MAKE_SHARED_INFO}
          />
          <CheckboxField
            id="createSubnet"
            label="Create Subnet"
            value={params.createSubnet}
            onChange={getParamsUpdater('createSubnet')}
            info="Create a subnet for this network now."
            tooltipAlign={{
              vertical: 'middle',
              horizontal: 'left',
            }}
          />
        </FormFieldSection>
        {params.createSubnet && (
          <>
            <BasicSubnetFields
              params={params}
              getParamsUpdater={getParamsUpdater}
              inNetworkCreation
            />
            <AdvancedSubnetFields params={params} updateParams={updateParams} />
          </>
        )}
      </>
    </ModalForm>
  )
}
